import { denormalizePoints, type Stroke } from '@awwyeah/shared';

/**
 * Canvas render helpers for SketchPad. Kept free of React so the geometry is unit-testable
 * (`buildStrokePath`) and the component only has to call `drawStroke`/`clearCanvas`.
 */

/** Sizes a canvas's backing store for devicePixelRatio crispness while keeping its CSS
 * (layout) size at `cssWidth`/`cssHeight`. Returns the 2D context, already `scale()`d so
 * every subsequent draw call can keep working in CSS pixels. */
export function fitCanvasToDpr(
  canvas: HTMLCanvasElement,
  cssWidth: number,
  cssHeight: number,
): CanvasRenderingContext2D | null {
  const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio ?? 1) : 1;
  const targetWidth = Math.max(1, Math.round(cssWidth * dpr));
  const targetHeight = Math.max(1, Math.round(cssHeight * dpr));
  if (canvas.width !== targetWidth) canvas.width = targetWidth;
  if (canvas.height !== targetHeight) canvas.height = targetHeight;
  canvas.style.width = `${cssWidth}px`;
  canvas.style.height = `${cssHeight}px`;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

/** Builds a `Path2D` from a stroke's normalized points, denormalized against the current
 * CSS canvas size. Pulled out of `drawStroke` so it can be unit-tested without a canvas. */
export function buildStrokePath(points: readonly number[], width: number, height: number): Path2D {
  const pixels = denormalizePoints(points, width, height);
  const path = new Path2D();
  if (pixels.length >= 2) {
    path.moveTo(pixels[0] ?? 0, pixels[1] ?? 0);
    for (let i = 2; i + 1 < pixels.length; i += 2) {
      path.lineTo(pixels[i] ?? 0, pixels[i + 1] ?? 0);
    }
  }
  return path;
}

export function drawStroke(
  ctx: CanvasRenderingContext2D,
  stroke: Pick<Stroke, 'points' | 'width' | 'color'>,
  cssWidth: number,
  cssHeight: number,
): void {
  const shortSide = Math.min(cssWidth, cssHeight);
  ctx.save();
  ctx.strokeStyle = stroke.color;
  ctx.lineWidth = Math.max(1, stroke.width * shortSide);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke(buildStrokePath(stroke.points, cssWidth, cssHeight));
  ctx.restore();
}

export function clearCanvas(
  ctx: CanvasRenderingContext2D,
  cssWidth: number,
  cssHeight: number,
): void {
  ctx.clearRect(0, 0, cssWidth, cssHeight);
}

/** Full redraw from a stroke list — fine at family scale (dozens of strokes), and simpler
 * than incremental diffing; callers requestAnimationFrame-batch this on every doc change. */
export function redrawAll(
  ctx: CanvasRenderingContext2D,
  strokes: readonly Stroke[],
  cssWidth: number,
  cssHeight: number,
): void {
  clearCanvas(ctx, cssWidth, cssHeight);
  for (const stroke of strokes) drawStroke(ctx, stroke, cssWidth, cssHeight);
}
